import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

const FAQS = [
  {
    id: 1,
    q: 'How do I place my order?',
    a: 'Simply add your favourite pieces to the cart and proceed to checkout — select your preferred payment method and confirm. It\'s that effortless.',
  },
  {
    id: 2,
    q: 'How long will delivery take?',
    a: 'We dispatch every order within 2 business days. Within India, your jewellery arrives in 3–7 days depending on your location. For international orders, please allow 1–3 weeks from the date of dispatch.',
  },
  {
    id: 3,
    q: 'Do you ship all over India?',
    a: 'Yes — we ship across every corner of India and to destinations worldwide. Wherever you are, Aamira Luxe finds its way to you.',
  },
  {
    id: 4,
    q: 'How can I track my order?',
    a: 'Once your order is dispatched, tracking details will be shared with you shortly. Please check back here soon for full tracking support.',
    pending: true,
  },
  {
    id: 5,
    q: 'What payment options are available? Do you offer international payments?',
    a: 'We are finalising our complete payment suite — including international options — to offer you the most seamless experience. Details coming soon.',
    pending: true,
  },
  {
    id: 6,
    q: 'Is there a return, refund or exchange policy?',
    a: 'Exchange requests must be raised within 24 hours of delivery. A continuous, unedited unboxing video is required for verification. If your requested item is in stock, we will process the exchange. If unavailable, we will issue a full refund of the product value.',
  },
  {
    id: 7,
    q: 'How can I reach your customer service?',
    a: 'Our customer care details are being set up to ensure you receive the best possible support. We\'ll have this ready for you very soon.',
    pending: true,
  },
];

const FAQItem = ({ item, index, isOpen, onToggle }) => {
  const itemRef = useRef(null);
  const inView = useInView(itemRef, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={itemRef}
      className="faq-item"
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ delay: index * 0.07, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <button
        className={`faq-question ${isOpen ? 'faq-question--open' : ''}`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="faq-num">0{item.id}</span>
        <span className="faq-q-text">{item.q}</span>
        <span className="faq-icon">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <motion.line
              x1="8" y1="2" x2="8" y2="14"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
              animate={{ scaleY: isOpen ? 0 : 1 }}
              transition={{ duration: 0.25 }}
              style={{ transformOrigin: 'center' }}
            />
            <line x1="2" y1="8" x2="14" y2="8"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            className="faq-answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.38, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="faq-answer-inner">
              {item.pending && (
                <span className="faq-pending-badge">Coming Soon</span>
              )}
              <p>{item.a}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQSection = () => {
  const [openId, setOpenId] = useState(null);
  const headingRef = useRef(null);
  const headingInView = useInView(headingRef, { once: true, margin: '-60px' });

  const toggle = (id) => setOpenId(prev => prev === id ? null : id);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        /* ── Section ── */
        .faq-section {
          position: relative;
          overflow: hidden;
          padding: 100px clamp(24px, 6vw, 100px);
          min-height: 600px;
        }

        /* Background image */
        .faq-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .faq-bg img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }

        /* Dark + gold overlay */
        .faq-overlay {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              135deg,
              rgba(14, 12, 10, 0.93) 0%,
              rgba(28, 20, 8, 0.88) 50%,
              rgba(14, 12, 10, 0.93) 100%
            );
          z-index: 1;
        }

        /* subtle gold shimmer top edge */
        .faq-overlay::after {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(to right, transparent, #B08D57 30%, #B08D57 70%, transparent);
          opacity: 0.6;
        }

        /* grain */
        .faq-grain {
          position: absolute;
          inset: 0;
          z-index: 2;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
          pointer-events: none;
        }

        /* ── Content wrapper ── */
        .faq-content {
          position: relative;
          z-index: 3;
          max-width: 820px;
          margin: 0 auto;
        }

        /* ── Header ── */
        .faq-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .faq-eyebrow {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 18px;
        }
        .faq-eyebrow::before,
        .faq-eyebrow::after {
          content: '';
          width: 32px; height: 1px;
          background: #B08D57;
          opacity: 0.6;
        }

        .faq-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(32px, 5vw, 54px);
          font-weight: 700;
          color: #FAF8F4;
          line-height: 1.08;
          letter-spacing: -0.01em;
          margin-bottom: 16px;
        }
        .faq-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }

        .faq-subtitle {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(15px, 1.8vw, 18px);
          font-style: italic;
          font-weight: 300;
          color: rgba(250, 248, 244, 0.55);
          letter-spacing: 0.03em;
          line-height: 1.7;
        }

        /* gold ornament divider */
        .faq-ornament {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin: 28px 0 0;
          opacity: 0.5;
        }
        .faq-ornament::before,
        .faq-ornament::after {
          content: '';
          width: 60px; height: 1px;
          background: linear-gradient(to right, transparent, #B08D57);
        }
        .faq-ornament::after {
          background: linear-gradient(to left, transparent, #B08D57);
        }
        .faq-diamond {
          width: 5px; height: 5px;
          background: #B08D57;
          transform: rotate(45deg);
        }

        /* ── FAQ List ── */
        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        /* ── Single item ── */
        .faq-item {
          border-bottom: 1px solid rgba(176, 141, 87, 0.18);
        }
        .faq-item:first-child {
          border-top: 1px solid rgba(176, 141, 87, 0.18);
        }

        .faq-question {
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          padding: 22px 0;
          display: flex;
          align-items: center;
          gap: 18px;
          text-align: left;
          transition: background 0.2s;
          position: relative;
        }
        .faq-question:hover .faq-q-text {
          color: #B08D57;
        }

        /* hover left gold bar */
        .faq-question::before {
          content: '';
          position: absolute;
          left: -clamp(24px, 6vw, 100px);
          top: 0; bottom: 0;
          width: 3px;
          background: #B08D57;
          transform: scaleY(0);
          transition: transform 0.25s ease;
          transform-origin: bottom;
        }
        .faq-question:hover::before,
        .faq-question--open::before {
          transform: scaleY(1);
        }

        .faq-num {
          font-family: 'Cormorant Garamond', serif;
          font-size: 11px;
          font-weight: 300;
          font-style: italic;
          color: #B08D57;
          letter-spacing: 0.08em;
          flex-shrink: 0;
          width: 22px;
          opacity: 0.7;
        }

        .faq-q-text {
          font-family: 'Jost', sans-serif;
          font-size: clamp(13px, 1.6vw, 15px);
          font-weight: 500;
          color: #FAF8F4;
          letter-spacing: 0.02em;
          line-height: 1.4;
          flex: 1;
          transition: color 0.2s;
        }
        .faq-question--open .faq-q-text {
          color: #B08D57;
        }

        .faq-icon {
          flex-shrink: 0;
          width: 30px; height: 30px;
          border: 1px solid rgba(176, 141, 87, 0.3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #B08D57;
          transition: background 0.2s, border-color 0.2s;
        }
        .faq-question--open .faq-icon {
          background: rgba(176, 141, 87, 0.12);
          border-color: rgba(176, 141, 87, 0.6);
        }

        /* Answer */
        .faq-answer {
          overflow: hidden;
        }
        .faq-answer-inner {
          padding: 0 0 24px 40px;
        }
        .faq-answer-inner p {
          font-family: 'Jost', sans-serif;
          font-size: clamp(13px, 1.4vw, 14px);
          font-weight: 300;
          color: rgba(250, 248, 244, 0.65);
          line-height: 1.85;
          letter-spacing: 0.025em;
          margin: 0;
        }

        .faq-pending-badge {
          display: inline-block;
          font-family: 'Jost', sans-serif;
          font-size: 8px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #B08D57;
          border: 1px solid rgba(176, 141, 87, 0.4);
          padding: 4px 10px;
          margin-bottom: 10px;
          border-radius: 1px;
        }

        /* ── Mobile ── */
        @media (max-width: 600px) {
          .faq-section { padding: 70px clamp(20px, 5vw, 32px); }
          .faq-answer-inner { padding-left: 28px; }
          .faq-question { gap: 12px; }
        }
      `}</style>

      <section className="faq-section">

        {/* Background jewelry image */}
        <div className="faq-bg">
          <img
            src="https://images.unsplash.com/photo-1584302179602-e4c3d3fd629d?w=1400&q=85"
            alt="jewellery background"
          />
        </div>

        {/* Dark overlay */}
        <div className="faq-overlay" />

        {/* Grain texture */}
        <div className="faq-grain" />

        {/* Content */}
        <div className="faq-content">

          {/* Header */}
          <div className="faq-header" ref={headingRef}>
            <motion.p
              className="faq-eyebrow"
              initial={{ opacity: 0 }}
              animate={headingInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6 }}
            >
              We're Here For You
            </motion.p>

            <motion.h2
              className="faq-title"
              initial={{ opacity: 0, y: 20 }}
              animate={headingInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.15, duration: 0.65 }}
            >
              Frequently Asked <em>Questions</em>
            </motion.h2>

            <motion.p
              className="faq-subtitle"
              initial={{ opacity: 0 }}
              animate={headingInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Everything you need to know before you adorn yourself.
            </motion.p>

            <motion.div
              className="faq-ornament"
              initial={{ opacity: 0 }}
              animate={headingInView ? { opacity: 0.5 } : {}}
              transition={{ delay: 0.45, duration: 0.6 }}
            >
              <div className="faq-diamond" />
            </motion.div>
          </div>

          {/* FAQ Accordion */}
          <div className="faq-list">
            {FAQS.map((item, i) => (
              <FAQItem
                key={item.id}
                item={item}
                index={i}
                isOpen={openId === item.id}
                onToggle={() => toggle(item.id)}
              />
            ))}
          </div>

        </div>
      </section>
    </>
  );
};

export default FAQSection;